import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import styles from './PermissionRequests.module.css';

interface PermissionRequest {
  id: number;
  userId: number;
  username: string;
  email: string;
  requestedFlag: string;
  reason: string;
  status: string;
  requestedAt: string;
  reviewedAt?: string;
  reviewedByUserId?: number;
  reviewedByUsername?: string;
  reviewNotes?: string;
}

interface NewRegistration {
  id: number;
  username: string;
  email: string;
  createdAt: string;
  discordName?: string;
  gameUserName?: string;
}

const PermissionRequests: React.FC = () => {
  const [requests, setRequests] = useState<PermissionRequest[]>([]);
  const [newRegistrations, setNewRegistrations] = useState<NewRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'registrations' | 'history'>('pending');
  const [processing, setProcessing] = useState<number | null>(null);
  const [isCSuite, setIsCSuite] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  useEffect(() => {
    checkCSuiteAccess();
  }, []);

  useEffect(() => {
    if (isCSuite) {
      // Fetch both datasets on initial load to show badge counts
      fetchRequests();
      fetchNewRegistrations();
    }
  }, [isCSuite]);

  useEffect(() => {
    // Refresh the active tab's data when switching tabs
    if (isCSuite) {
      if (activeTab === 'registrations') {
        fetchNewRegistrations();
      } else {
        fetchRequests();
      }
    }
  }, [activeTab]);

  const checkCSuiteAccess = async () => {
    try {
      const res = await api.get('/auth/user/profile');
      const role = res.data?.role || '';
      const position = res.data?.primaryPosition?.id;
      
      // C-Suite position IDs: CEO (4), CHRO (5), COO (11)
      const cSuitePositionIds = [4, 5, 11];
      const hasAccess = role === 'Admin' || (position && cSuitePositionIds.includes(position));
      
      setIsCSuite(hasAccess);
      if (!hasAccess) {
        alert('❌ Access Denied: This page is restricted to C-Suite leadership.');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Failed to check C-Suite access', err);
      window.location.href = '/dashboard';
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const statusFilter = activeTab === 'pending' ? 'pending' : undefined;
      const url = statusFilter ? `/permissionrequests?status=${statusFilter}` : '/permissionrequests';
      const res = await api.get(url);
      setRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch permission requests', err);
      alert('Failed to load permission requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchNewRegistrations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/user/prospects');
      setNewRegistrations(res.data || []);
    } catch (err) {
      console.error('Failed to fetch new registrations', err);
      alert('Failed to load new registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptMember = async (userId: number, username: string) => {
    if (!window.confirm(`Accept ${username} as an Organization Member?\n\nThis will:\n• Change their role to Member\n• Grant OrgMember flag`)) {
      return;
    }

    setProcessing(userId);
    try {
      await api.post(`/auth/user/${userId}/accept-member`);
      alert(`✅ ${username} has been accepted as an Organization Member!`);
      fetchNewRegistrations();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to accept member';
      alert('❌ ' + msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleApprove = async (requestId: number) => {
    const notes = window.prompt('Optional approval notes:');
    if (notes === null) return; // User cancelled

    setProcessing(requestId);
    try {
      await api.post(`/permissionrequests/${requestId}/approve`, {
        reviewNotes: notes || undefined
      });
      alert('✅ Permission request approved! Flag has been granted to the user.');
      fetchRequests();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to approve request';
      alert('❌ ' + msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleDeny = async (requestId: number) => {
    const notes = window.prompt('Please provide a reason for denying this request:');
    if (!notes || notes.trim() === '') {
      alert('⚠️ Denial reason is required.');
      return;
    }

    setProcessing(requestId);
    try {
      await api.post(`/permissionrequests/${requestId}/deny`, {
        reviewNotes: notes.trim()
      });
      alert('✅ Permission request denied.');
      fetchRequests();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to deny request';
      alert('❌ ' + msg);
    } finally {
      setProcessing(null);
    }
  };

  const formatFlag = (flag: string): string => {
    switch (flag) {
      case 'CanEditEvents': return 'Can Edit Events';
      case 'CanManageUsers': return 'Can Manage Users';
      case 'canCreatePosts': return 'Can Create Posts';
      case 'viewAllDepartments': return 'View All Departments';
      case 'OrgMember': return 'Organization Member';
      case 'AccessMedicalReports': return 'Access Medical Reports';
      case 'CanAcceptMissions': return 'Can Accept Missions';
      default: return flag;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending':
        return <span className={styles.statusPending}>⏳ Pending</span>;
      case 'Approved':
        return <span className={styles.statusApproved}>✓ Approved</span>;
      case 'Denied':
        return <span className={styles.statusDenied}>✗ Denied</span>;
      default:
        return <span>{status}</span>;
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'Pending');
  const historyRequests = requests.filter(r => r.status !== 'Pending');

  const displayedRequests = activeTab === 'pending' ? pendingRequests : historyRequests;

  if (!isCSuite) {
    return <div className={styles.pageContainer}>Loading...</div>;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>🔐 Approval Requests</h1>
            <div className={styles.subtitle}>Review and approve member requests</div>
          </div>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewButton} ${viewMode === 'cards' ? styles.viewButtonActive : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              ⊞
            </button>
            <button
              className={`${styles.viewButton} ${viewMode === 'list' ? styles.viewButtonActive : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              ≡
            </button>
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'pending' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Permission Requests {pendingRequests.length > 0 && <span className={styles.badge}>{pendingRequests.length}</span>}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'registrations' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('registrations')}
          >
            New Registrations {newRegistrations.length > 0 && <span className={styles.badge}>{newRegistrations.length}</span>}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'history' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {/* Instructional Banner */}
        {activeTab === 'pending' && (
          <div className={styles.instructionBanner}>
            <div className={styles.bannerIcon}>ℹ️</div>
            <div className={styles.bannerContent}>
              <h3 className={styles.bannerTitle}>Permission Request Guidelines</h3>
              <p className={styles.bannerText}>
                Members can request various permissions to access additional features within the organization. 
                Review each request to ensure it aligns with the member's role and responsibilities. Common requests include:
              </p>
              <ul className={styles.bannerList}>
                <li><strong>Can Accept Missions:</strong> Allows member to accept and complete organization missions</li>
                <li><strong>Can Edit Events:</strong> Grants ability to create and modify organization events</li>
                <li><strong>Can Create Posts:</strong> Enables posting content to organization feeds</li>
                <li><strong>Access Medical Reports:</strong> Provides access to medical SOS reports and data</li>
              </ul>
              <p className={styles.bannerAction}>
                <strong>Action Required:</strong> Review the member's reason, verify their role, then Approve ✓ or Deny ✗ the request.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'registrations' && (
          <div className={styles.instructionBanner}>
            <div className={styles.bannerIcon}>📋</div>
            <div className={styles.bannerContent}>
              <h3 className={styles.bannerTitle}>New Member Registration Approval</h3>
              <p className={styles.bannerText}>
                New users who have registered on the platform are awaiting approval to become full Organization Members.
              </p>
              <p className={styles.bannerAction}>
                <strong>Action Required:</strong> Before accepting, verify that the registrant has completed ALL onboarding steps:
              </p>
              <ul className={styles.bannerList}>
                <li>✓ Successfully registered on the organization's RSI website page</li>
                <li>✓ Verified their identity on the organization's Discord channel</li>
                <li>✓ Provided valid Star Citizen handle and Discord name</li>
              </ul>
              <p className={styles.bannerNote}>
                <strong>Note:</strong> Once accepted, the user will receive the "Member" role and gain access to member-only features.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className={styles.instructionBanner}>
            <div className={styles.bannerIcon}>📜</div>
            <div className={styles.bannerContent}>
              <h3 className={styles.bannerTitle}>Request History</h3>
              <p className={styles.bannerText}>
                View all previously processed permission requests. This archive includes both approved and denied requests, 
                along with review notes and the C-Suite member who processed each request.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : activeTab === 'registrations' ? (
          newRegistrations.length === 0 ? (
            <div className={styles.emptyState}>✓ No new registrations</div>
          ) : viewMode === 'list' ? (
            <div className={styles.listView}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>SC Handle</th>
                    <th>Discord</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {newRegistrations.map(reg => (
                    <tr key={reg.id}>
                      <td className={styles.username}>{reg.username}</td>
                      <td className={styles.email}>{reg.email}</td>
                      <td>{reg.gameUserName || '-'}</td>
                      <td>{reg.discordName || '-'}</td>
                      <td className={styles.date}>{formatDate(reg.createdAt)}</td>
                      <td>
                        <button
                          className={styles.approveButtonSmall}
                          onClick={() => handleAcceptMember(reg.id, reg.username)}
                          disabled={processing === reg.id}
                        >
                          {processing === reg.id ? '⏳' : '✓ Accept'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.requestsList}>
              {newRegistrations.map(reg => (
                <div key={reg.id} className={styles.requestCardCompact}>
                  <div className={styles.compactHeader}>
                    <div className={styles.userInfo}>
                      <div className={styles.username}>{reg.username}</div>
                      <div className={styles.email}>{reg.email}</div>
                    </div>
                    <button
                      className={styles.approveButton}
                      onClick={() => handleAcceptMember(reg.id, reg.username)}
                      disabled={processing === reg.id}
                    >
                      {processing === reg.id ? '⏳ Processing...' : '✓ Accept'}
                    </button>
                  </div>
                  <div className={styles.compactInfo}>
                    {reg.gameUserName && <span>🎮 {reg.gameUserName}</span>}
                    {reg.discordName && <span>💬 {reg.discordName}</span>}
                    <span>📅 {formatDate(reg.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : displayedRequests.length === 0 ? (
          <div className={styles.emptyState}>
            {activeTab === 'pending' ? '✓ No pending requests' : 'No request history'}
          </div>
        ) : viewMode === 'list' ? (
          <div className={styles.listView}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Permission</th>
                  <th>Reason</th>
                  <th>Requested</th>
                  <th>Status</th>
                  {activeTab === 'pending' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {displayedRequests.map(request => (
                  <tr key={request.id}>
                    <td className={styles.username}>{request.username}</td>
                    <td className={styles.email}>{request.email}</td>
                    <td><span className={styles.flagBadge}>{formatFlag(request.requestedFlag)}</span></td>
                    <td className={styles.reasonCell}>{request.reason}</td>
                    <td className={styles.date}>{formatDate(request.requestedAt)}</td>
                    <td>{getStatusBadge(request.status)}</td>
                    {activeTab === 'pending' && (
                      <td>
                        <div className={styles.tableActions}>
                          <button
                            className={styles.approveButtonSmall}
                            onClick={() => handleApprove(request.id)}
                            disabled={processing === request.id}
                          >
                            {processing === request.id ? '⏳' : '✓'}
                          </button>
                          <button
                            className={styles.denyButtonSmall}
                            onClick={() => handleDeny(request.id)}
                            disabled={processing === request.id}
                          >
                            {processing === request.id ? '⏳' : '✗'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.requestsList}>
            {displayedRequests.map(request => (
              <div key={request.id} className={styles.requestCardCompact}>
                <div className={styles.compactHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.username}>{request.username}</div>
                    <div className={styles.email}>{request.email}</div>
                  </div>
                  <div className={styles.compactRight}>
                    {getStatusBadge(request.status)}
                    {request.status === 'Pending' && (
                      <div className={styles.compactActions}>
                        <button
                          className={styles.approveButton}
                          onClick={() => handleApprove(request.id)}
                          disabled={processing === request.id}
                        >
                          {processing === request.id ? '⏳' : '✓'}
                        </button>
                        <button
                          className={styles.denyButton}
                          onClick={() => handleDeny(request.id)}
                          disabled={processing === request.id}
                        >
                          {processing === request.id ? '⏳' : '✗'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.compactInfo}>
                  <span className={styles.flagBadge}>{formatFlag(request.requestedFlag)}</span>
                  <span className={styles.reason}>{request.reason}</span>
                  <span className={styles.date}>📅 {formatDate(request.requestedAt)}</span>
                  {request.reviewedAt && (
                    <span className={styles.reviewInfo}>Reviewed by {request.reviewedByUsername || 'Unknown'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionRequests;
